import PageContainer from "../components/layout/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { billingApi } from "../api/billing";
import { endpointsApi } from "../api/endpoints";
import { formatBytes } from "../utils/format";

export default function Usage() {
  const usage = useApi(() => billingApi.getUsage(), []);
  const endpoints = useApi(() => endpointsApi.list(), []);

  if (usage.loading || endpoints.loading) {
    return <LoadingSpinner message="Loading usage data..." />;
  }

  const usageData = usage.data?.data;
  const endpointList = endpoints.data?.data ?? [];

  return (
    <PageContainer
      title="Usage"
      description="Data transfer and usage across all endpoints"
    >
      <div className="space-y-6">
        {/* Account-wide usage summary */}
        {usage.error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-700">
              Unable to load usage data: {usage.error}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-medium text-gray-900">
              Account Data Transfer
            </h2>
            {usageData ? (
              <UsageSummary data={usageData} />
            ) : (
              <p className="text-sm text-gray-500">No usage data available</p>
            )}
          </div>
        )}

        {/* Per-endpoint usage */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-medium text-gray-900">
              Per-Endpoint Usage
            </h2>
          </div>
          {endpointList.length === 0 ? (
            <p className="p-4 text-center text-sm text-gray-500">
              No endpoints configured
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {endpointList.map((ep) => (
                <EndpointUsageRow key={ep.id} endpointId={ep.id} name={ep.name || ep.iccid} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

function UsageSummary({ data }: { data: Record<string, unknown> }) {
  // Stacuity returns dynamic fields — render whatever we get
  const entries = Object.entries(data).filter(
    ([, v]) => typeof v === "number" || typeof v === "string",
  );

  if (entries.length === 0) {
    return <p className="text-sm text-gray-500">No usage data available</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(([key, value]) => (
        <div key={key} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
          <p className="text-xs font-medium uppercase text-gray-500">
            {key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim()}
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {typeof value === "number" && key.toLowerCase().includes("byte")
              ? formatBytes(value)
              : String(value)}
          </p>
        </div>
      ))}
    </div>
  );
}

function EndpointUsageRow({
  endpointId,
  name,
}: {
  endpointId: string;
  name: string;
}) {
  const usage = useApi(
    () => billingApi.getEndpointUsage(endpointId),
    [endpointId],
  );

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900">{name}</p>
        {usage.loading && (
          <span className="text-xs text-gray-400">Loading...</span>
        )}
      </div>
      {usage.error && (
        <p className="mt-1 text-xs text-amber-600">{usage.error}</p>
      )}
      {usage.data?.data && (
        <div className="mt-2 flex flex-wrap gap-4">
          {Object.entries(usage.data.data)
            .filter(([, v]) => typeof v === "number" || typeof v === "string")
            .slice(0, 4)
            .map(([key, value]) => (
              <div key={key} className="text-xs">
                <span className="text-gray-500">
                  {key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim()}:
                </span>{" "}
                <span className="font-medium text-gray-900">
                  {typeof value === "number" && key.toLowerCase().includes("byte")
                    ? formatBytes(value)
                    : String(value)}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
